import { getCollegePriority } from './collegePriority.js';
import { matchesSelection } from './filterSelection.js';

const branchPriorityGroups = [
  [
    'IT',
    'I.T.',
    'INFORMATIONTECHNOLOGY',
    'CSE',
    'COMPUTERSCIENCE',
    'COMPUTERSC',
    'COMPUTERSCIENCEANDENGINEERING',
    'COMPUTERSCIENCEANDTECHNOLOGY',
    'AI&ML',
    'AIML',
    'ARTIFICIALINTELLIGENCE&MACHINELEARNING',
    'ARTIFICIALINTELLIGENCE',
    'CSE-DS',
    'CSEDS',
    'DATASCIENCE',
    'CSE-CY',
    'CSECY',
    'CYBERSECURITY',
  ],
  [
    'ECE',
    'ELECTRO&COMMUNICATIONENGINEERING',
    'ELECTROANDCOMMUNICATIONENGINEERING',
    'ELECTRONICSENGINEERING',
    'ELECTRONICSENGG',
    'EEE',
    'ELECTRICAL&ELECTRONICSENGINEERING',
    'EE',
    'ELECTRICALENGINEERING',
  ],
  [
    'CIVIL',
    'CIVILENGINEERING',
    'MECHANICAL',
    'MECHANICALENGINEERING',
  ],
  [
    'ROBOTICS',
    'ROBOTICSANDAUTOMATION',
    'BIOMEDICAL',
    'BIOMEDICALROBOTICENGINEERING',
    'BIOINFORMATICS',
    'MINING',
    'MININGENGINEERING',
    'MININGENGG',
    'FIRESAFETY',
    'FIRETECHNOLOGY&SAFETY',
    'FOODTECH',
    'FOODTECHNOLOGY',
    'FOODTECHNOLOGYANDMANAGEMENT',
    'DAIRYTECH',
  ],
  ['3DANIMATION', '3-DANIMATION&GRAPHICS'],
];

const normalizeBranch = (value = '') => value.toUpperCase().replace(/[^A-Z0-9]+/g, '');

const branchPriorityMap = branchPriorityGroups.reduce((map, group, groupIndex) => {
  group.forEach((branch, branchIndex) => {
    map.set(normalizeBranch(branch), groupIndex * 100 + branchIndex);
  });

  return map;
}, new Map());

const maxBranchPriority = Math.max(...branchPriorityMap.values());
const minBranchPriority = Math.min(...branchPriorityMap.values());

const COLLEGE_PRIORITY_FALLBACK = 9999;
const COLLEGE_PRIORITY_MAX = 320;
const COLLEGE_PRIORITY_MIN = 0;

export const getBranchPriority = (branch = '', course = '') => {
  const branchKey = normalizeBranch(branch);
  const courseKey = normalizeBranch(course);

  const directMatch =
    branchPriorityMap.get(branchKey) ??
    branchPriorityMap.get(courseKey);

  if (directMatch !== undefined) {
    return directMatch;
  }

  for (const [pattern, priority] of branchPriorityMap.entries()) {
    if (branchKey.includes(pattern) || courseKey.includes(pattern)) {
      return priority;
    }
  }

  return 9999;
};

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const getNormalizedPriorityScore = (priority, minimum, maximum, fallback = 9999) => {
  if (priority === fallback || priority === Number.POSITIVE_INFINITY) {
    return 0;
  }

  if (maximum === minimum) {
    return 100;
  }

  return clamp(((maximum - priority) / (maximum - minimum)) * 100, 0, 100);
};

const getNormalizedValueScore = (value, minimum, maximum, fallback = Number.POSITIVE_INFINITY) => {
  if (value === fallback || value === Number.POSITIVE_INFINITY) {
    return 0;
  }

  if (maximum === minimum) {
    return 100;
  }

  return clamp(((value - minimum) / (maximum - minimum)) * 100, 0, 100);
};

const statusSortOrder = {
  'LOW CHANCE': 0,
  BORDERLINE: 1,
  SAFE: 2,
  'NO CHANCE': 3,
};

const ADAPTIVE_THRESHOLDS = {
  SAFE: 0.92,
  BORDERLINE: 1.03,
  'LOW CHANCE': 1.07,
  'LOW CHANCE DISPLAY': 1.055,
  'NO CHANCE': 1.07,
};

export const getAdaptivePredictionScore = ({ rankUsed, closingRank }) => {
  if (!rankUsed || !closingRank) {
    return Number.POSITIVE_INFINITY;
  }

  const rankRatio = rankUsed / closingRank;
  const buffer = 0.05 * Math.max(0, 1 - rankRatio);

  return rankRatio + buffer;
};

export const getPredictionStatus = ({ rankUsed, closingRank }) => {
  const adaptiveScore = getAdaptivePredictionScore({ rankUsed, closingRank });

  if (adaptiveScore <= ADAPTIVE_THRESHOLDS.SAFE) return 'SAFE';
  if (adaptiveScore <= ADAPTIVE_THRESHOLDS.BORDERLINE) return 'BORDERLINE';
  if (adaptiveScore <= ADAPTIVE_THRESHOLDS['LOW CHANCE']) return 'LOW CHANCE';
  return 'NO CHANCE';
};

export const getCutoffScore = ({ rankUsed, closingRank }) => {
  const adaptiveScore = getAdaptivePredictionScore({ rankUsed, closingRank });

  if (!Number.isFinite(adaptiveScore)) {
    return 0;
  }

  if (adaptiveScore <= ADAPTIVE_THRESHOLDS.SAFE) {
    const progress = clamp(adaptiveScore / ADAPTIVE_THRESHOLDS.SAFE, 0, 1);
    return 85 + (1 - progress) * 15;
  }

  if (adaptiveScore <= ADAPTIVE_THRESHOLDS.BORDERLINE) {
    const progress = clamp(
      (adaptiveScore - ADAPTIVE_THRESHOLDS.SAFE) /
        (ADAPTIVE_THRESHOLDS.BORDERLINE - ADAPTIVE_THRESHOLDS.SAFE),
      0,
      1,
    );
    return 65 + (1 - progress) * 20;
  }

  if (adaptiveScore <= ADAPTIVE_THRESHOLDS['LOW CHANCE']) {
    const progress = clamp(
      (adaptiveScore - ADAPTIVE_THRESHOLDS.BORDERLINE) /
        (ADAPTIVE_THRESHOLDS['LOW CHANCE'] - ADAPTIVE_THRESHOLDS.BORDERLINE),
      0,
      1,
    );
    return 40 + (1 - progress) * 25;
  }

  return Math.max(0, 40 - (adaptiveScore - ADAPTIVE_THRESHOLDS['LOW CHANCE']) * 100);
};

export const getPredictorBadge = ({ cutoffScore, collegeScore, branchScore, weightedScore }) => {
  if (weightedScore >= 85 || (cutoffScore >= 85 && collegeScore >= 75 && branchScore >= 70)) {
    return 'Best Match';
  }

  if (branchScore >= 85 && cutoffScore >= 60) {
    return 'High Demand';
  }

  if (collegeScore >= 85 && cutoffScore >= 55) {
    return 'Top College';
  }

  return 'Balanced Choice';
};

export const getPredictorWeightedScore = ({
  cutoffScore,
  collegePriority,
  branchPriority,
}) => {
  const collegeScore = getNormalizedPriorityScore(
    collegePriority,
    COLLEGE_PRIORITY_MIN,
    COLLEGE_PRIORITY_MAX,
    COLLEGE_PRIORITY_FALLBACK,
  );
  const branchScore = getNormalizedPriorityScore(
    branchPriority,
    minBranchPriority,
    maxBranchPriority,
    9999,
  );

  const weightedScore = Number(
    (cutoffScore * 0.5 + collegeScore * 0.3 + branchScore * 0.2).toFixed(2),
  );

  return {
    cutoffScore,
    collegeScore,
    branchScore,
    weightedScore,
    badge: getPredictorBadge({ cutoffScore, collegeScore, branchScore, weightedScore }),
  };
};

export const sortByPredictorScore = (results) =>
  [...results].sort((first, second) => {
    return (
      (statusSortOrder[first.status] ?? 99) - (statusSortOrder[second.status] ?? 99) ||
      first.closingRank - second.closingRank ||
      first.collegePriority - second.collegePriority ||
      first.branchPriority - second.branchPriority ||
      second.gap - first.gap ||
      second.weightedScore - first.weightedScore ||
      second.cutoffScore - first.cutoffScore
    );
  });

export const getRangeBreakdownFromClosingRank = (closingRank) => {
  if (!closingRank) {
    return {
      safe: '-',
      borderline: '-',
      lowChance: '-',
      noChance: '-',
    };
  }

  const safeMax = Math.max(
    1,
    Math.floor((((ADAPTIVE_THRESHOLDS.SAFE - 0.05) / 0.95) * closingRank)),
  );
  const borderlineMax = Math.max(safeMax + 1, Math.ceil(ADAPTIVE_THRESHOLDS.BORDERLINE * closingRank));
  const lowChanceMax = Math.max(
    borderlineMax + 1,
    Math.ceil(ADAPTIVE_THRESHOLDS['LOW CHANCE DISPLAY'] * closingRank),
  );

  return {
    safe: `<= ${safeMax.toLocaleString('en-IN')}`,
    borderline: `${(safeMax + 1).toLocaleString('en-IN')} - ${borderlineMax.toLocaleString('en-IN')}`,
    lowChance: `${(borderlineMax + 1).toLocaleString('en-IN')} - ${lowChanceMax.toLocaleString('en-IN')}`,
    noChance: `> ${lowChanceMax.toLocaleString('en-IN')}`,
  };
};

export const predictColleges = ({
  data,
  generalRank,
  categoryRank,
  category,
  gender,
  rcgRank,
  femaleGeneralRank,
  femaleCategoryRank,
  ownership = 'All',
  college = 'All',
  branch = 'All',
  sortBy = 'collegePriority',
}) => {
  const numericGeneralRank = Number(generalRank);
  const numericCategoryRank = Number(categoryRank);
  const resolvedSeatType = gender === 'Female' ? 'All' : 'General';

  if (!numericGeneralRank || numericGeneralRank <= 0) {
    return [];
  }

  const filteredResults = data
    .filter((item) => {
      const cat = item.category;
      const isRcgCat = cat === 'RCG' || cat === 'E-RCG';

      if (gender === 'Female') {
        const canCompeteForRcg = category !== 'UR' && category !== 'EWS';
        if (isRcgCat) {
          return canCompeteForRcg;
        }
        if (category === 'UR') {
          return cat === 'UR' || cat === 'E-UR';
        }
        return (
          cat === 'UR' ||
          cat === 'E-UR' ||
          cat === category ||
          cat === `E-${category}`
        );
      } else {
        if (category === 'UR') {
          return cat === 'UR' || cat === 'E-UR';
        }
        return cat === 'UR' || cat === 'E-UR' || cat === category;
      }
    })
    .filter((item) => resolvedSeatType === 'All' || item.seatType === resolvedSeatType)
    .filter((item) => ownership === 'All' || item.ownership === ownership)
    .filter((item) => matchesSelection(item.college, college))
    .filter((item) => matchesSelection(item.branch, branch))
    .map((item) => {
      let rankUsed = Number.POSITIVE_INFINITY;
      const cat = item.category;

      if (gender === 'Female') {
        const isFemaleSeat = item.seatType === 'Female';
        const isRCG = cat === 'RCG' || cat === 'E-RCG';
        const isFemaleSpecific = isFemaleSeat || isRCG;

        if (isFemaleSpecific) {
          if (cat === 'UR' || cat === 'E-UR') {
            rankUsed = Number(femaleGeneralRank) || numericGeneralRank || Number.POSITIVE_INFINITY;
          } else if (isRCG) {
            rankUsed = Number(rcgRank) || Number(femaleGeneralRank) || numericGeneralRank || Number.POSITIVE_INFINITY;
          } else if (cat === category || cat === `E-${category}`) {
            rankUsed = Number(femaleCategoryRank) || numericCategoryRank || Number.POSITIVE_INFINITY;
          } else {
            rankUsed = numericCategoryRank || Number.POSITIVE_INFINITY;
          }
        } else {
          if (cat === 'UR' || cat === 'E-UR') {
            rankUsed = numericGeneralRank;
          } else if (isRCG) {
            rankUsed = Number(rcgRank) || numericGeneralRank;
          } else if (cat === category) {
            rankUsed = numericCategoryRank || Number.POSITIVE_INFINITY;
          }
        }
      } else {
        if (cat === 'UR' || cat === 'E-UR') {
          rankUsed = numericGeneralRank;
        } else if (cat === category) {
          rankUsed = numericCategoryRank || Number.POSITIVE_INFINITY;
        }
      }

      const rankType = cat === 'UR' ? 'General Rank' : 'Category Rank';
      const quotaCode = item.seatType === 'Female' ? `F_${item.category}` : item.category;
      const gap = item.closingRank - rankUsed;

      return {
        ...item,
        gap,
        collegePriority: getCollegePriority(item.college),
        branchPriority: getBranchPriority(item.branch, item.course),
        rankUsed,
        rankType,
        quotaCode,
        status: getPredictionStatus({
          rankUsed,
          closingRank: item.closingRank,
        }),
      };
    });

  const maxClosingRank = Math.max(...filteredResults.map((item) => item.closingRank), 0);
  const minClosingRank = Math.min(...filteredResults.map((item) => item.closingRank), maxClosingRank);

  const scoredResults = filteredResults.map((item) => {
    const cutoffStrengthScore = getNormalizedValueScore(
      item.closingRank,
      minClosingRank,
      maxClosingRank,
      Number.POSITIVE_INFINITY,
    );
    const chanceFitScore = getCutoffScore({
      rankUsed: item.rankUsed,
      closingRank: item.closingRank,
    });
    const cutoffScore = Number((cutoffStrengthScore * 0.75 + chanceFitScore * 0.25).toFixed(2));
    const weightedScoreDetails = getPredictorWeightedScore({
      cutoffScore,
      collegePriority: item.collegePriority,
      branchPriority: item.branchPriority,
    });

    return {
      ...item,
      cutoffStrengthScore,
      chanceFitScore,
      ...weightedScoreDetails,
    };
  });

  if (sortBy === 'collegePriority') {
    return sortByPredictorScore(scoredResults);
  }

  if (sortBy === 'rank') {
    return [...scoredResults].sort((first, second) => first.closingRank - second.closingRank);
  }

  return [...scoredResults].sort((first, second) => second.gap - first.gap);
};
